import React, {useState} from 'react'
import PropTypes from 'prop-types'
import {TextInput, Dropdown} from '@/components/atoms'
import ProductSelectionModal from './ProductSelectionModal'
import {BONUS_PRODUCTS_DROPDOWN_OPTIONS, BONUS_PRODUCTS_DROPDOWN_OPTIONS_WITH_GTIN} from './config'
import styles from '../RewardSetup.module.scss'
import {showGtinFeature} from '@/utils/commons'
import {useAppSelector} from '@/store'

/**
 * BonusProductsCondition component displays the bonus products reward condition UI
 * Handles product selection via modal triggered by dropdown option selection, order quantity, and bonus product limit inputs
 *
 * @param {Object} props - Component props
 * @param {Object} props.formData - Form data containing selectedProducts, orderQuantity, bonusProductLimit
 * @param {Object} props.validation - Validation state object
 * @param {boolean} props.bonusProductsLoading - Loading state for bonus products
 * @param {Function} props.handleProductsChange - Handler for products selection changes
 * @param {Function} props.handleOrderQuantityChange - Handler for order quantity changes
 * @param {Function} props.handleBonusProductLimitChange - Handler for bonus product limit changes
 * @param {string} props.orderQuantityError - Error message for order quantity validation
 * @param {string} props.bonusProductLimitError - Error message for bonus product limit validation
 * @returns {JSX.Element} The rendered BonusProductsCondition component
 */
const BonusProductsCondition = ({
  formData,
  validation,
  bonusProductsLoading,
  handleProductsChange,
  handleOrderQuantityChange,
  handleBonusProductLimitChange,
  orderQuantityError,
  bonusProductLimitError,
}) => {
  const [showProductModal, setShowProductModal] = useState(false)
  
  // Handle dropdown option selection
  const handleDropdownClick = selectedValue => {

    // Filter out Product ID and GTIN options from the selected value array
    // These are action options, not actual product selections
    const filteredSelectedValue = selectedValue
      ? selectedValue.filter(item => item.id !== 'productId' && item.id !== 'productId/gtin')
      : []
    // Pass the filtered array to handleProductsChange
    handleProductsChange(filteredSelectedValue)

    // Check if Product ID or GTIN options were selected for modal/action handling
    if (selectedValue && selectedValue.length > 0) {
     setShowProductModal(true)
    }
  }
  
  // Handle closing the product selection modal
  const handleCloseProductModal = () => {
    setShowProductModal(false)
  }
  
  // Handle product selection from modal
  const handleProductSelection = (selectedProductsData) => {
    // Convert selected products to the format expected by the parent component
    // The parent expects an array of objects with value and label properties
    const selectedProductValues = selectedProductsData.map(product => ({
      value: product.productId,
      label: product.productName
    }))
    
    handleProductsChange(selectedProductValues)
  }
  const countryCode = useAppSelector(state => state.countryCode).countryCode
  
  // Determine if GTIN feature should be shown based on country code
  const showGtin = showGtinFeature(countryCode)

  // Choose dropdown options based on GTIN feature flag
  const bonusProductDropdownOptions = showGtin
    ? BONUS_PRODUCTS_DROPDOWN_OPTIONS_WITH_GTIN
    : BONUS_PRODUCTS_DROPDOWN_OPTIONS

  // Create display options for dropdown based on selected products
  // formData.selectedProducts now stores full product objects with value and label
  const selectedProductOptions = formData.selectedProducts || []
  
  return (
    <div className="flex flex-wrap gap-[13px]">
      <div className={`${styles.rewardSetup__productsDropdownWrapper} text-left`}>
        <Dropdown
          className='placeholder:text-sm text-sm'
          value={selectedProductOptions} // Show selected products
          isError={validation.selectedProductsValid}
          errorMessage={validation.selectedProductsValid ? 'At least one product must be selected' : ''}
          isDark={true}
          childList={bonusProductDropdownOptions}
          placeholder={bonusProductsLoading ? "Loading..." : "Select Products"}
          isJsonWrap={false}
          selection='multiple'
          hideMenuListOnSelection={true}
          isClearable={false}
          updateValue={handleDropdownClick}
        />
      </div>
      <span className="text-sm whitespace-nowrap mt-2">free after every</span>
      <div className={`w-32 ${styles.rewardSetup__inputPoints}`}>
        <TextInput
          className='placeholder:text-sm text-sm'
          value={formData.orderQuantity}
          onChange={handleOrderQuantityChange}
          label=""
          placeholderText="Qty"
          error={orderQuantityError}
          isDark={true}
          type="number"
          id="order-quantity"
        />
      </div>
      <span className="text-sm whitespace-nowrap mt-2">orders, with a limit of</span>
      <div className={`w-32 ${styles.rewardSetup__inputPoints}`}>
        <TextInput
          className='placeholder:text-sm text-sm'
          value={formData.bonusProductLimit}
          onChange={handleBonusProductLimitChange}
          label=""
          placeholderText="Qty"
          error={bonusProductLimitError}
          isDark={true}
          type="number"
          id="bonus-product-limit"
        />
      </div>
      <span className="text-sm whitespace-nowrap mt-2">bonus products.</span>

      {/* Product Selection Modal */}
      <ProductSelectionModal
        showModal={showProductModal}
        onCloseModal={handleCloseProductModal}
        onSelectProducts={handleProductSelection}
        selectedProducts={
          formData.selectedProducts?.map(p => ({
            productId: p.value,
            productName: p.displayText
          })) || []
        }
      />
    </div>
  )
}

BonusProductsCondition.propTypes = {
  formData: PropTypes.shape({
    selectedProducts: PropTypes.array,
    orderQuantity: PropTypes.string,
    bonusProductLimit: PropTypes.string,
  }).isRequired,
  validation: PropTypes.shape({
    selectedProductsValid: PropTypes.bool,
  }).isRequired,
  bonusProductsLoading: PropTypes.bool.isRequired,
  handleProductsChange: PropTypes.func.isRequired,
  handleOrderQuantityChange: PropTypes.func.isRequired,
  handleBonusProductLimitChange: PropTypes.func.isRequired,
  orderQuantityError: PropTypes.string,
  bonusProductLimitError: PropTypes.string,
}

export default BonusProductsCondition
